<template>
  <HighlightsWidget
    :disableZeroState="true"
    :isLoading="false"
    title="Device Overview"
  >
    <template v-slot:mainContent>
      <div
        :class="[
          $style.overviewDetailList,
          isInsightsInfoActive ? $style.overviewDetailFullList : ''
        ]"
      >
        <div
          v-for="field in deviceFieldsToShow"
          :key="field.name"
          :class="$style.overviewDetail"
        >
          <HeadingExtraSmall :hasMargin="false">
            {{ field.name }}
          </HeadingExtraSmall>
          <ParagraphBody :hasMargin="false">
            {{ field.value || '-' }}
          </ParagraphBody>
        </div>
      </div>
    </template>
  </HighlightsWidget>
</template>

<script>
import { HeadingExtraSmall, ParagraphBody } from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModuleMap, osFamilyTypes } from 'client/util/Constants';
import { capitalize, isEmpty } from 'lodash';

import { mapActions, mapGetters } from 'vuex';
import DateUtil from 'client/util/DateUtil';
import HighlightsWidget from 'client/components/Highlights/HighlightsWidget';
import Util from 'client/util/Util';

const {
  android, darwin, ios, linux, windows,
} = osFamilyTypes;

const systemCollection = 'SystemUnacknowledgedCollection';
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

export default {
  name: 'DeviceOverviewWidget',

  components: { HeadingExtraSmall, HighlightsWidget, ParagraphBody },

  props: {
    item: {
      type: Object,
      default: () => {},
    },
  },

  data: () => ({
    systemInfo: {},
  }),

  computed: {
    ...mapGetters(settingsModule, ['hasSystemInsights']),
    ...mapGetters('SystemCollection', {
      getSystemNIC: 'getSystemNIC',
    }),

    agentBasedDeviceFields() {
      const columns = [
        {
          name: 'OS',
          value: this.osName,
        },
        {
          name: 'Device Name',
          value: this.item.displayName,
        },
        {
          name: 'Serial Number',
          value: this.item.serialNumber,
        },
        {
          name: 'Public IP Address',
          value: this.item.remoteIP,
        },
        {
          name: 'Private IP Address',
          value: this.getSystemNIC(this.item),
        },
        {
          name: 'Agent Status',
          value: this.item.active ? 'Active' : 'Inactive',
        },
        {
          name: 'Last Contact',
          value: DateUtil.formatDateTime(this.item.lastContact),
        },
        {
          name: 'Created',
          value: DateUtil.formatDateTime(this.item.created),
        },
      ];

      if (this.isInsightsInfoActive) {
        // Vendor column
        columns.splice(4, 0, {
          name: 'Vendor',
          value: this.systemInfo.hardware_vendor,
        });

        // Model column
        columns.splice(5, 0, {
          name: 'Model',
          value: this.systemInfo.hardware_model,
        });

        // CPU column
        columns.push({
          name: 'CPU',
          value: this.systemInfo.cpu_brand,
        });

        // Memory column
        columns.push({
          name: 'Memory',
          value: this.physicalMemory,
        });
      }

      return columns;
    },

    deviceFieldsToShow() {
      return this.isAgentBasedDevice ? this.agentBasedDeviceFields : this.nonAgentBasedDeviceFields;
    },

    isAgentBasedDevice() {
      const { osFamily } = this.item;
      return !(osFamily === ios || osFamily === android);
    },

    isInsightsInfoActive() {
      return !isEmpty(this.systemInfo);
    },

    nonAgentBasedDeviceFields() {
      return [
        {
          name: 'OS',
          value: this.osName,
        },
        {
          name: 'Device Name',
          value: this.item.displayName,
        },
        {
          name: 'Created',
          value: DateUtil.formatDateTime(this.item.created),
        },
        {
          name: 'Enrollment type',
          value: this.isAgentBasedDevice ? '' : capitalize(this.item.mdm.enrollmentType),
        },
      ];
    },

    osName() {
      let osName = '';
      switch (this.item.osFamily) {
        case windows:
          osName = `${this.item.osVersionDetail.osName} ${this.item.version} (${this.item.osVersionDetail.patch}.${this.item.osVersionDetail.revision}) `;
          break;
        case darwin:
          osName = `${this.item.osVersionDetail.osName} ${this.item.osVersionDetail.releaseName} ${this.item.version} (${this.item.osVersionDetail.revision})`;
          break;
        case linux:
          if (this.item.os === 'RedHat') {
            osName = `${this.item.os} ${this.item.version} (${this.item.osVersionDetail.releaseName})`;
          } else {
            osName = `${this.item.os || 'Linux'} ${this.item.version}`;
          }
          break;
        case ios:
          osName = `${this.item.os} ${this.item.version}`;
          break;
        case android:
          osName = `${this.item.os} ${this.item.version}`;
          break;
        default:
          osName = '-';
          break;
      }
      return osName;
    },

    physicalMemory() {
      // Convert bytes to GB
      const { number, unit } = Util.getTextToDisplayBytes(
        this.systemInfo.physical_memory || 0, true,
      );
      return `${number} ${unit}`;
    },

    publicIpAddress() {
      let networkInterface = '';
      if (this.item.networkInterfaces.length) {
        networkInterface = this.item.networkInterfaces
          .find(({ family, name }) => (family === 'IPv4' && name.includes('en')));
      }

      return networkInterface ? networkInterface.address : '-';
    },
  },

  created() {
    this.handleLoadSystemInsightsData();
  },

  methods: {
    ...mapActions(systemCollection, [
      'fetchSystemInsightsData',
    ]),

    async handleLoadSystemInsightsData() {
      if (this.hasSystemInsights && this.item.systemInsights.state === 'enabled') {
        this.systemInfo = await this.fetchSystemInsightsData({ id: this.item.id, endpoint: 'system_info' });
      }
    },
  },
};
</script>

<style module scoped>
@value (max-width-xx-large) from '~css/breakpoints.css';

.overviewDetail {
  border-bottom: 1px solid var(--jcGrey100);
  display: flex;
  justify-content: space-between;
  margin: 0 30px 13px 0;
}

.overviewDetailList {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-height: 12rem;
}

.overviewDetailFullList {
  max-height: 17rem;
}

@media (max-width: max-width-xx-large) {
  .overviewDetailList { max-height: 100%; }
}
</style>
